export type ProductItem = {
  title: string;
  image: string;
  description?: string;
};

export type ProductCategory = {
  id: string;
  title: string;
  subtitle: string;
  heroImage: string;
  products: ProductItem[];
};

export const productCategories: Record<string, ProductCategory> = {
  'steel-plant': {
    id: 'steel-plant',
    title: 'Steel Plant Equipment',
    subtitle: 'At Velmurugan Industries we undertake a wide range of projects for major steel plants across India and have also exported overseas. We offer comprehensive services, including fabrication, machining, painting, piping, assembly, run testing, and packing of the equipment. This end-to-end approach ensures that each piece of equipment is delivered fully operational and ready for integration into the plant. By managing all aspects of production in-house, Velmurugan Industries maintains strict quality control and ensures timely delivery, making us a trusted partner for the steel industry.',
    heroImage: '/products/steel-plant-home.jpg',
    products: [
      {
        title: 'Lance Equipment',
        image: '/products/steel-plant/Lance-Trolley.jpg',
        description: 'Lance equipment, often referred to as a lance or oxygen lance, is a tool commonly used in steelmaking and other high-temperature industrial processes. It is essentially a pipe or tube designed to deliver gases, such as oxygen or fuel, directly into a furnace or molten metal. The high-temperature environment requires the lance to be made from materials that can withstand extreme heat and corrosion.',
      },
      {
        title: 'Vacuum Vessel',
        image: '/products/steel-plant/Vacuum-Vessel.jpeg',
        description: 'A vacuum vessel in a steel plant is a critical component used in secondary steelmaking processes, particularly in vacuum degassing. Vacuum degassing is a process that removes unwanted gases, such as hydrogen, nitrogen, and oxygen, from molten steel to improve its quality.',
      },
      {
        title: 'Hot Off Take',
        image: '/products/steel-plant/Hot-off-Take.jpg',
        description: 'The term "hot off-take" in a steel plant generally refers to a system or process where gases, heat, or materials are taken directly from high-temperature processes.',
      },
      {
        title: 'Tundish Car',
        image: '/products/steel-plant/Tundish-Car.jpg',
        description: 'A tundish car is a crucial component in the continuous casting process of steel production. It is a mobile platform or carriage designed to transport and position the tundish—a vessel that holds and distributes molten steel from the ladle to the Mold—over the casting area.',
      },
      {
        title: 'Vessel Transfer Car',
        image: '/products/steel-plant/Vessel-Transfer-Car.jpg',
        description: 'A vessel transfer car is a specialized piece of equipment used in steel plants and other heavy industries to transport large vessels, such as ladles, tundishes, or converters, between different stages of the production process.',
      },
      {
        title: 'Tilting Platform',
        image: '/products/steel-plant/Tilting-platform.jpg',
        description: 'A tilting platform in a steel plant is a specialized piece of equipment used to adjust the angle of large vessels or containers, such as ladles, converters, or tundishes, during various stages of the steelmaking process.',
      },
      {
        title: 'Segment Frame',
        image: '/products/steel-plant/Segment-Frame.jpg',
        description: 'A segment frame is a key structural component used in the continuous casting process in steel plants.',
      },
    ],
  },
  'cement-plant': {
    id: 'cement-plant',
    title: 'Cement Plant Equipment',
    subtitle: 'Velmurugan Industries has delivered a range of components for cement plants, catering to both domestic and international needs. Our expertise includes manufacturing to both work on vertical and horizontal mill, where we manage the entire process, including material preparation, preprocessing, fabrication, machining, assembly, and finishing with blasting and painting. This comprehensive approach ensures high-quality and reliable components for cement plant operations.',
    heroImage: '/products/cement-plant-home.jpg',
    products: [
      {
        title: 'HRP Frames',
        image: '/products/cement-plant/hrp-frame_2-1.jpg',
        description: 'HRP frames typically refer to Hydraulic Roller Press (HRP) frames in the context of cement plants. The HRP is a type of grinding equipment used for pre-grinding and finish grinding in cement production. It is highly efficient and helps reduce energy consumption in the grinding process.',
      },
      {
        title: 'Main Bearing',
        image: '/products/cement-plant/MULTIPAD-MAIN-BEARING-.jpg',
        description: 'In a cement plant, the main bearing assembly plays a critical role in supporting the rotary kiln or mill, ensuring smooth rotation and efficient operation. At VMIL we have manufactured 3 different types of main bearing: 1. Hydro static trunnion bearing 2. Multiped main bearing 3. Hydro dynamic trunnion bearing',
      },
      {
        title: 'Burner Trolley',
        image: '/products/cement-plant/burner-troly.jpg',
        description: 'A burner trolley is a specialized component used in rotary kilns, primarily in cement plants and other industrial processes that involve high-temperature treatment of materials.',
      },
      {
        title: 'Jaw Crusher',
        image: '/products/cement-plant/Jaw-Crusher.png',
        description: 'A jaw crusher is a type of crushing equipment used in various industries, including mining, construction, and recycling, to break down large rocks or other materials into smaller, more manageable pieces.',
      },
      {
        title: 'Classifier',
        image: '/products/cement-plant/clasifier1.jpg',
        description: 'In a cement plant, a classifier is a critical component used to separate and control the particle size distribution of the ground materials, such as cement powder.',
      },
      {
        title: 'Mould Wall Assembly',
        image: '/products/cement-plant/mould-wall-assy-scaled.jpg',
        description: 'In a cement plant, a mould wall assembly refers to the structural components used in the manufacturing of pre-cast concrete products or in forming concrete shapes, such as blocks, pipes, or slabs.',
      },
    ],
  },
  'mineral-handling': {
    id: 'mineral-handling',
    title: 'Mineral Handling Equipment',
    subtitle: 'Velmurugan Industries manufactured a huge number of mineral handling equipment for both India and overseas.',
    heroImage: '/products/mineral-handling-home.jpg',
    products: [
      {
        title: 'Stacker Reclaimer',
        image: '/products/mineral-handling/Stacker-Reclaimer.jpg',
        description: 'A stacker reclaimer is a large, automated piece of equipment used in bulk material handling systems, particularly in industries such as mining, cement, steel, and power generation.',
      },
      {
        title: 'Feeder Breaker',
        image: '/products/mineral-handling/Feeder-Breaker.jpg',
        description: 'A feeder breaker is a heavy-duty machine commonly used in mining operations for primary crushing and material handling. It combines two functions—feeding and breaking—into one machine.',
      },
      {
        title: 'Apron Feeder',
        image: '/products/mineral-handling/Apron-Feeder.jpg',
        description: 'An apron feeder is a type of mechanical equipment used to transport heavy, large, and abrasive materials in industries such as mining, cement, quarrying, and mineral processing.',
      },
      {
        title: 'Barrel Reclaimer',
        image: '/products/mineral-handling/Barrel-Reclaimer.jpg',
        description: 'A barrel reclaimer is a type of bulk material handling equipment used primarily in industries such as mining, power generation, steel production, and cement manufacturing. Its main function is to reclaim bulk materials from storage piles and feed them back into the production process.',
      },
      {
        title: 'Hammer Crusher',
        image: '/products/mineral-handling/Hammer-Crusher.jpg',
        description: 'A hammer crusher is a type of industrial equipment used for crushing and reducing materials into smaller sizes.',
      },
      {
        title: 'Bogie Wheel Equalizer',
        image: '/products/mineral-handling/Bogie-wheels-equalizer-scaled.jpg',
        description: 'An equalizing beam, equalizing lever or equalizing bar links the suspension of two or more adjacent axles of a vehicle with more than two axles, especially railway locomotives. Its job is to provide compensated springing to ensure an even and statically determinate distribution of load to all the axles on uneven terrain or poorly laid track.',
      },
    ],
  },
  'defence': {
    id: 'defence',
    title: 'Defence Equipment',
    subtitle: 'At Velmurugan Industries, we had the esteemed opportunity to contribute to significant projects for the Indian Navy, including the fabrication of the submarine reactor body and the helicopter landing pad. These projects exemplify our technical prowess, precision engineering, and commitment to delivering high-quality solutions.',
    heroImage: '/products/boiler-defence-home.jpg',
    products: [
      {
        title: 'Helicopter Landing Grid',
        image: '/products/defence/Helicopter-Landing-Grid-1.jpg',
        description: 'Fabricated for the Indian Navy, these helicopter landing pads exemplify precision engineering and commitment to delivering high-quality defence solutions.',
      },
    ],
  },
  'renewable-energy': {
    id: 'renewable-energy',
    title: 'Renewable Energy',
    subtitle: 'Velmurugan Industries has contributed significantly to the wind industry by manufacturing several key components. In addition to their expertise in heavy fabrication, the company has developed specialized parts that are critical to the performance and reliability of wind turbines.',
    heroImage: '/products/renewable-energy-home.jpg',
    products: [
      {
        title: 'Rear Frames',
        image: '/products/renewable-energy/Rear-Frame.jpg',
        description: 'Velmurugan Industries fabricates robust nacelle frames that provide structural support for key turbine components such as the gearbox, generator, and control systems. These frames are designed to withstand the mechanical stresses and environmental challenges of wind turbine operation.',
      },
      {
        title: 'Rotor Hub Components',
        image: '/products/renewable-energy/Hub-Internal-support-struct.jpg',
        description: 'Velmurugan Industries has experience in producing rotor hubs, which connect the turbine blades to the main shaft. These components are engineered for precise load distribution and optimal aerodynamic performance.',
      },
      {
        title: 'Tower Sections and Door Segment',
        image: '/products/renewable-energy/Guide-Ring-1.jpg',
        description: 'The company also manufactures tower sections, ensuring that they meet the stringent requirements for strength and durability. These sections are essential for supporting the nacelle and rotor assembly, especially in tall turbines designed to capture higher wind speeds.',
      },
      {
        title: 'Custom Lifting and Handling Devices',
        image: '/products/renewable-energy/Guide-Ring-1.jpg',
        description: 'In addition to standard turbine components, Velmurugan Industries designs and fabricates custom-built lifting tools used in the installation and maintenance of wind turbines like short bridges. These tools are critical for safely handling large and heavy components during assembly and repair work.',
      },
    ],
  },
  'boiler-components': {
    id: 'boiler-components',
    title: 'Boiler Components',
    subtitle: 'Velmurugan Industries has an extensive portfolio of boiler components, including FGRS panels, gravimetric feeders, and boiler drums with higher wall thickness, high pressure piping, water wall panels, swage tubes.',
    heroImage: '/products/boiler-defence-home.jpg',
    products: [
      {
        title: 'FGRS Panel',
        image: '/products/boiler-components/FGRS-PANEL-1.jpg',
        description: 'Velmurugan Industries has successfully and indigenously manufactured a Fuel Gas Resection Panel 500 MW Lignite Fired Tower Type Boilers. This significant achievement involved intricate engineering and fabrication, showcasing the company\'s capability to handle complex boiler components.',
      },
      {
        title: 'Gravimetric Feeder',
        image: '/products/boiler-components/Gravic-Metric-Feeder-36.jpg',
        description: 'It is a key component for Thermal Power Station, VMIL supplied 400 no\'s in a single year. VMIL scope includes Fabrication, Machining, Surface Preparation, Mechanical Assembly, Electrical Interfacing and Calibration.',
      },
      {
        title: 'Boiler Drum',
        image: '/products/boiler-components/Boiler-Drum.jpg',
        description: 'A steam drum is a standard feature of a water-tube boiler. It is a reservoir of water/steam at the top end of the water tubes. The drum stores the steam generated in the water tubes and acts as a phase-separator for the steam/water mixture. The difference in densities between hot and cold water helps in the accumulation of the "hotter"-water/and saturated-steam into the steam-drum.',
      },
      {
        title: 'Calciner Cooler Tube Bundle',
        image: '/products/boiler-components/Tube-Bundle-for-Calciner-Cooler.jpg',
        description: 'It is used in heat exchanger this type of heat exchanger consists of a tube bundle inserted inside a shell. One of the fluids circulates inside the tubes and other around them inside shell. The number of baffles and their geometry are optimized in order to improve the heat transfer. It is used in an Aluminum plant equipment.',
      },
    ],
  },
};
